import BenchlingConfig from 'BenchlingConfig';
import {useCanvasVersionFileState} from 'canvas-core/backbone/useCanvasVersionFileState';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';

import type {
  ChatCanvasVersionQuery,
  ChatCanvasVersionQueryVariables,
} from './__generated/ChatCanvasVersionQuery';
import {CHAT_CANVAS_VERSION_QUERY} from './ChatCanvasVersionGraphQL';

export interface ChatCanvasVersionSummary {
  name: string;
  versionIndex: number;
}

export type ChatCanvasVersionStateResult =
  | {type: 'skipped'}
  | {type: 'pending'}
  | {type: 'error'}
  | {type: 'success'; canvasVersion: ChatCanvasVersionSummary};

/**
 * Loads the title and version label of a canvas version shown in a chat.
 *
 * With a chat session, the details are fetched through the chat, so anyone who can view the chat
 * (including share-link viewers) can see them without direct access to the canvas version. Without
 * one, the viewer's own access to the canvas version is used.
 */
export function useChatCanvasVersionState(
  chatSessionId: string | null,
  canvasDefinitionId: string,
  canvasVersionId: string
): ChatCanvasVersionStateResult {
  const chatQueryResult = useSkippableBenchlingQuery<ChatCanvasVersionQuery, ChatCanvasVersionQueryVariables>(
    CHAT_CANVAS_VERSION_QUERY,
    {
      skip: !BenchlingConfig.ENABLE_CANVAS_CORE || chatSessionId == null,
      variables: {chatSessionId: chatSessionId ?? '', canvasVersionId},
    }
  );
  const directResult = useCanvasVersionFileState(canvasDefinitionId, canvasVersionId, chatSessionId == null);

  if (chatSessionId == null) {
    if (directResult.type !== 'success') {
      return directResult;
    }
    return {
      type: 'success',
      canvasVersion: {
        name: directResult.canvasVersion.definition.name,
        versionIndex: directResult.canvasVersion.versionIndex,
      },
    };
  }

  if (chatQueryResult.type === QueryResultType.SKIPPED) {
    return {type: 'skipped'};
  }
  if (chatQueryResult.type === QueryResultType.PENDING) {
    return {type: 'pending'};
  }
  if (chatQueryResult.type === QueryResultType.ERROR) {
    return {type: 'error'};
  }

  const {chatCanvasVersion} = chatQueryResult.data;
  if (isInaccessible(chatCanvasVersion) || chatCanvasVersion.definitionId !== canvasDefinitionId) {
    return {type: 'error'};
  }
  return {
    type: 'success',
    canvasVersion: {name: chatCanvasVersion.definitionName, versionIndex: chatCanvasVersion.versionIndex},
  };
}
